/* eslint-disable jsx-a11y/click-events-have-key-events */
import { mergeClasses } from '@livv/utils/helpers';
import { SxProps, Typography } from '@mui/material';
import {
    TreeItem,
    TreeItemProps,
    useTreeItemState,
    TreeItemContentProps,
    treeItemClasses,
} from '@mui/x-tree-view/TreeItem';
import { FC, forwardRef, MouseEvent as ReactMouseEvent, Ref } from 'react';
import { sxs } from '@utils/mui/sx';

const treeItemSx: SxProps = {
    // Label
    [`& .${treeItemClasses.label}:not(.highlighted)`]: {
        color: 'grey.100',
        overflow: 'hidden',
        whiteSpace: 'normal',
    },
    // Content (node)
    [`& .${treeItemClasses.content}`]: {
        borderRadius: 0.5,
        mb: 0.5,
        py: 0.5,
    },
    // Group (nodes group)
    [`& .${treeItemClasses.groupTransition}`]: {
        borderLeft: '1px dashed',
        borderLeftColor: 'grey.400',
        ml: 2,
        pl: 2,
    },
    // Expanded node
    [`& .${treeItemClasses.expanded}`]: {
        [`.${treeItemClasses.label}`]: {
            fontWeight: 'medium',
        },
        '&:hover': {
            backgroundColor: 'rgba(147, 113, 44, 0.12)',
        },
        backgroundColor: 'grey.800',
        border: '1px solid',
        borderColor: 'grey.600',
    },
    // Selected node
    [`& .${treeItemClasses.selected},
        .${treeItemClasses.selected}.${treeItemClasses.content},
        .${treeItemClasses.selected}.${treeItemClasses.focused},
        .${treeItemClasses.selected}.${treeItemClasses.content}.${treeItemClasses.focused}
        `]: {
        [`.${treeItemClasses.label}`]: {
            fontWeight: 'medium',
        },
        backgroundColor: 'primary.light',
        border: '1px solid',
        borderColor: 'primary.main',
    },
    // Search higlight
    '& .searchHighlight': {
        backgroundColor: 'action.selected',
    },
    // We have a problem when we click an element in tree, another is focuses without being the one selected
    // it adds a background style we do not want to see, so we remove it with styling
    // TODO: find why the original problem occurs
    [`& .${treeItemClasses.focused}:not(.${treeItemClasses.selected})`]: {
        backgroundColor: 'unset',
    },
    [`& .${treeItemClasses.focused}`]: {
        backgroundColor: 'primary.light',
    },
    // Global
    color: 'grey.400',
};

const CustomContent: FC<TreeItemContentProps> = forwardRef(
    (
        { classes, className, itemId, icon: iconProp, expansionIcon, displayIcon, label, onClick },
        ref,
    ) => {
        const icon = iconProp || expansionIcon || displayIcon;

        const { disabled, expanded, selected, focused, handleExpansion, handleSelection } =
            useTreeItemState(itemId);

        const handleSelectionClick = (event: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
            if (onClick) onClick(event);
            handleSelection(event);
        };

        return (
            <div
                ref={ref as Ref<HTMLDivElement>}
                className={mergeClasses(className, classes.root, {
                    [classes.expanded]: expanded,
                    [classes.selected]: selected,
                    [classes.focused]: focused,
                    [classes.disabled]: disabled,
                })}
            >
                {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
                <div className={classes.iconContainer} onClick={handleExpansion} role="button">
                    {icon}
                </div>
                <Typography className={classes.label} onClick={handleSelectionClick}>
                    {label}
                </Typography>
            </div>
        );
    },
);

const CustomTreeItem: FC<TreeItemProps> = ({ sx = {}, ...props }) => (
    <TreeItem ContentComponent={CustomContent} sx={sxs([treeItemSx, sx])} {...props} />
);
export default CustomTreeItem;
